/**
 * Button — Botão com variantes, loading, ícone e fullWidth
 */
import type { JSX } from 'preact';
import { T, CT } from '../../styles/tokens';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';

interface ButtonProps {
  children?: any;
  variant?: ButtonVariant;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  onClick?: (e: any) => void;
  onMouseEnter?: (e: any) => void;
  onMouseLeave?: (e: any) => void;
  disabled?: boolean;
  title?: string;
  style?: JSX.CSSProperties;
  className?: string;
  type?: string;
  loading?: boolean;
  icon?: any;
  fullWidth?: boolean;
  [key: string]: any;
}

export function Button({ children, variant = 'primary', size = 'md', onClick, onMouseEnter, onMouseLeave, disabled, title, style, className, type, loading, icon, fullWidth, ...rest }: ButtonProps) {
  const sizeStyles: Record<string, JSX.CSSProperties> = {
    xs: { padding: '4px 10px', fontSize: T.textXs, borderRadius: 6 },
    sm: { padding: '6px 12px', fontSize: T.textSm, borderRadius: CT.button.radius },
    md: { padding: CT.button.padding, fontSize: CT.button.fontSize, borderRadius: 10 },
    lg: { padding: '14px 32px', fontSize: 15, borderRadius: T.radiusLg },
  };

  const variantStyles: Record<string, JSX.CSSProperties> = {
    primary: {
      background: 'var(--gradient-primary)', color: CT.button.text, border: 'none',
      boxShadow: '0 2px 12px rgba(109,76,255,0.3)',
    },
    secondary: {
      background: CT.button.secondaryBg, color: CT.button.secondaryText,
      border: `1px solid ${T.primaryBg}`,
    },
    ghost: {
      background: CT.button.ghostBg, color: CT.button.ghostText,
      border: `1px solid ${T.border}`,
    },
    danger: {
      background: CT.button.dangerBg, color: CT.button.dangerText, border: 'none',
    },
    success: {
      background: T.success, color: T.text, border: 'none',
    },
  };

  const isDisabled = disabled || loading;

  return (
    <button
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      disabled={isDisabled}
      title={title}
      type={type as any}
      class={className}
      {...rest}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: T.space2,
        fontWeight: CT.button.fontWeight, cursor: isDisabled ? 'not-allowed' : 'pointer',
        transition: CT.button.transition,
        opacity: isDisabled ? 0.5 : 1,
        width: fullWidth ? '100%' : undefined,
        justifyContent: fullWidth ? 'center' : undefined,
        ...sizeStyles[size],
        ...variantStyles[variant],
        ...style,
      }}
    >
      {loading ? (
        <span style={{
          width: 14, height: 14,
          border: `2px solid ${T.whiteAlpha30}`,
          borderTopColor: '#fff',
          borderRadius: '50%',
          animation: 'spin 0.6s linear infinite',
          flexShrink: 0,
        }} />
      ) : icon ? (
        <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{icon}</span>
      ) : null}
      {children}
    </button>
  );
}
